import { compose } from 'redux';
import { connect } from 'react-redux';
import selectors from '../../../rootSelectors';
import {
    clearResponses,
    initResponsesStructure,
    completeMazeAssessment,
    setIsMazeUntimed,
} from '../../mazeActions';
import withAvatar from '../../../common/avatar/withAvatar';
import { stopAudio } from '../../../common/audio/audioActions';
import {
    unmountScreen,
    enableTextPaneScroll,
    disableTextPaneScroll,
    blockScreen,
    unblockScreen,
    greyOutScreen,
    colorInScreen,
    greyOutOkButton,
    colorInOkButton,
} from '../../../common/screen/screenActions';
import { dataDogAddWarning } from '../../../common/logging/datadog';
import { apiCallFailed } from '../../../common/api/apiCallActions';
import { LANTERN_ERROR_CODES } from '../../../constants/error';
import { onResponseSelected } from './MazePassageActions';
import { goToInterstitial } from '../../../common/navigation/navigationActions';
import {
    startTimeTracking,
    stopTimeTracking,
} from '../../../common/timeTracking/timeTrackingActions';
import { MAZE_PASSAGE_DURATION } from '../../../common/timeTracking/timeTrackingKeys';
import AvatarView from '../../../common/avatar/avatarView';
import { getAvatarPosition } from '../../../common/alex/AlexUtils';
import MazePassageView from './MazePassageView';
import { setLanguageForMeasure } from '../../../common/utils/languageUtils';

const mapStateToProps = (state, ownProps) => ({
    isScreenBlocked: selectors.getIsScreenBlocked(state),
    isGreyedOut: selectors.getIsScreenGreyedOut(state),
    isOkButtonGreyedOut: selectors.getIsOkButtonGreyedOut(state),
    isNextButtonActive: selectors.getIsNextButtonActive(state),
    isDataFetched: selectors.getIsMazeDataFetched(state),
    paragraphs: selectors.getMazeParagraphs(state),
    questions: selectors.getMazeQuestionItems(state),
    title: selectors.getMazeTitle(state),
    selectedResponses: selectors.getMazeSelectedResponses(state),
    isScrollEnabled: selectors.getIsTextPaneScrollEnabled(state),
    isExtendedView: ownProps.isExtendedView,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
    fetchContent: () => {
        dispatch(ownProps.flowStrategy.fetchContent());
        setLanguageForMeasure(dispatch, ownProps.flowStrategy);
    },
    initializePassage: () => {
        dispatch(enableTextPaneScroll());
        dispatch(initResponsesStructure());
        dispatch(startTimeTracking(MAZE_PASSAGE_DURATION));
    },
    onResponseSelected: (questionId, responseId) => {
        dispatch(onResponseSelected(questionId, responseId));
    },
    cleanup: () => {
        dispatch(stopAudio());
        dispatch(clearResponses());
        dispatch(disableTextPaneScroll());
        dispatch(unmountScreen());
    },
    onNext: (isDiscontinued, isRetry = false) => {
        dispatch(blockScreen());
        dispatch(greyOutScreen());
        dispatch(greyOutOkButton());
        dispatch(stopTimeTracking(MAZE_PASSAGE_DURATION));
        return dispatch(ownProps.flowStrategy.saveResponse(isDiscontinued, isRetry))
            .then(() => {
                dispatch(completeMazeAssessment(ownProps.flowStrategy.assessment));
                dispatch(unblockScreen());
                dispatch(goToInterstitial());
            })
            .catch(error => {
                // STEAM-8604: the save rejected, leaving the screen blocked/greyed
                // with no way to recover. Restore it to an interactive state.
                dispatch(unblockScreen());
                dispatch(colorInScreen());
                dispatch(colorInOkButton());
                // Only surface the "raise your hand" / retry popup if a lower
                // layer hasn't already shown an error (e.g. the offline-result
                // popup) — otherwise we'd override it with a second popup. Its
                // Retry button re-runs onNext(isDiscontinued, true).
                dispatch((thunkDispatch, getState) => {
                    const state = getState();
                    const alreadyHandled =
                        selectors.getScreenLevelErrorStatus(state) !== undefined ||
                        selectors.getAppLevelErrorStatus(state) !== undefined;
                    if (!alreadyHandled) {
                        thunkDispatch(
                            apiCallFailed(
                                undefined,
                                undefined,
                                error?.message || String(error),
                                LANTERN_ERROR_CODES.CONNECTION_TIMEOUT,
                            ),
                        );
                    }
                });
                dataDogAddWarning('Maze passage saveResponse failed', {
                    isDiscontinued,
                    isRetry,
                    // Only log a safe string — never the raw error object.
                    error: error?.message || String(error),
                });
            });
    },
    setIsMazeUntimed: isMazeUntimed => {
        dispatch(setIsMazeUntimed(isMazeUntimed));
    },
});

const avatarMapStateToProps = (_, ownProps) => {
    const { isExtendedView } = ownProps;
    const positionConfig = {
        alyxPosition: AvatarView.Position.TOP_HEADER_CENTER,
        extendedViewPosition: AvatarView.Position.LOWER_FAR_LEFT,
        defaultViewPosition: AvatarView.Position.LOWER_LEFT,
    };
    const position = getAvatarPosition(positionConfig, isExtendedView);
    return {
        position,
        instructions: ownProps.flowStrategy.instructions.passageInstruction,
        animateAlex: true,
    };
};

const MazePassageContainer = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAvatar(avatarMapStateToProps),
)(MazePassageView);

export default MazePassageContainer;
